import { useState } from "react";
import { useForm } from "react-hook-form";
import { useOutletContext } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { formatDistanceToNowStrict } from "date-fns";
import { mutate } from "swr";
import * as yup from "yup";
import { Button } from "~/components/ui/button";
import { Label } from "~/components/ui/label";
import { Textarea } from "~/components/ui/textarea";
import useToast from "~/components/ui/toast";
import { usePrompt } from "~/hooks/usePrompt";
import { AvatarStack } from "~/pages/manifold-nextgen/components/AvatarStack";
import { EditableTitle } from "~/pages/manifold-nextgen/components/EditableTitle";
import {
  OverviewTile,
  OverviewTiles,
} from "~/pages/manifold-nextgen/components/EntityOverviewHeader";
import { useAgentShare } from "~/pages/manifold-nextgen/hooks/useAgentShare";
import { useCanEditAgent } from "~/pages/manifold-nextgen/hooks/useCanEditAgent";
import { useOwners } from "~/pages/manifold-nextgen/hooks/useOwners";
import { Agent, AgentRole, AgentScope, agentPath, updateAgent } from "~/services/agents";
import { ACCESS_LEVEL_LABEL } from "./AgentAccessBadge";
import { AgentAvatar } from "./AgentAvatar";

const UNSAVED_CHANGES_MESSAGE = "You have unsaved changes. Leave without saving?";

// Lets the Save button (rendered in the header) submit this form by id.
const FORM_ID = "agent-profile-form";

const schema = yup.object({
  description: yup.string().defined(),
  persona: yup.string().defined(),
  instructions: yup.string().defined(),
});

type FormValues = yup.InferType<typeof schema>;

export const AgentProfileTab = () => {
  const agent = useOutletContext<Agent>();
  const isEditable = useCanEditAgent(agent);
  const isSystemAgent = agent.scope === AgentScope.SYSTEM;
  const { showSuccess, showError } = useToast();
  const [isSaving, setIsSaving] = useState(false);

  // System agents aren't shared per-principal, so skip the grants fetch for them.
  const { collaborators, accessLevels } = useAgentShare(agent.id, !isSystemAgent);

  const owners = useOwners(collaborators, accessLevels, AgentRole.IS_MANAGER);

  const tiles: OverviewTile[] = [
    {
      label: "My Role",
      content: agent.access_level ? ACCESS_LEVEL_LABEL[agent.access_level] : "—",
    },
    {
      label: "Owners",
      content: <AvatarStack items={owners} />,
    },
    {
      label: "Type",
      content: isSystemAgent ? "System" : "Custom",
    },
    {
      label: "Last Updated",
      content: agent.updated_at
        ? formatDistanceToNowStrict(new Date(agent.updated_at), { addSuffix: true })
        : "—",
    },
  ];

  const {
    register,
    handleSubmit,
    reset,
    formState: { isDirty, isValid },
  } = useForm<FormValues>({
    resolver: yupResolver(schema),
    mode: "onChange",
    defaultValues: {
      description: agent.description ?? "",
      persona: agent.persona ?? "",
      instructions: agent.instructions ?? "",
    },
  });

  // Warn before navigating away (tab switch, browser close/refresh) with unsaved edits.
  usePrompt(isDirty ? UNSAVED_CHANGES_MESSAGE : false, { beforeUnload: true });

  const doSave = async (values: FormValues) => {
    setIsSaving(true);
    try {
      await updateAgent(agent.id, {
        description: values.description,
        persona: values.persona,
        instructions: values.instructions,
      });
      reset(values);
      await mutate(agentPath(agent.id));
      showSuccess("Agent saved");
    } catch {
      showError("Failed to save agent");
    } finally {
      setIsSaving(false);
    }
  };

  // Name edits commit immediately (inline, separate from the form's Save button).
  const handleRename = async (next: string) => {
    try {
      await updateAgent(agent.id, { display_name: next });
      await mutate(agentPath(agent.id));
      showSuccess("Agent renamed");
    } catch (e) {
      showError("Failed to rename agent");
      throw e;
    }
  };

  const saveAction = isEditable ? (
    <div className="flex items-center gap-2">
      <Button variant="ghost" size="sm" onClick={() => reset()} disabled={!isDirty || isSaving}>
        Discard
      </Button>
      <Button
        type="submit"
        form={FORM_ID}
        size="sm"
        disabled={!isDirty || !isValid || isSaving}
        loading={isSaving}
      >
        Save
      </Button>
    </div>
  ) : undefined;

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-5">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <AgentAvatar id={agent.id} name={agent.name} size={48} />
          <EditableTitle
            value={agent.display_name}
            canEdit={isEditable}
            label="Agent name"
            onSave={handleRename}
          />
        </div>
        {saveAction}
      </div>

      <OverviewTiles tiles={tiles} />

      <form id={FORM_ID} onSubmit={handleSubmit(doSave)} className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5">
          <Label htmlFor="description">Description</Label>
          <Textarea
            id="description"
            rows={3}
            disabled={!isEditable || isSaving}
            {...register("description")}
          />
        </div>

        {(agent.persona || isEditable) && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="persona">Persona</Label>
            <Textarea
              id="persona"
              rows={10}
              disabled={!isEditable || isSaving}
              className="resize-none"
              {...register("persona")}
            />
          </div>
        )}

        {(agent.instructions || isEditable) && (
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="instructions">Instructions</Label>
            <Textarea
              id="instructions"
              rows={10}
              disabled={!isEditable || isSaving}
              className="resize-none"
              {...register("instructions")}
            />
          </div>
        )}
      </form>
    </div>
  );
};
